import React, { Suspense, useState, useEffect, useCallback, useRef } from 'react';
import { connect } from 'react-redux';
import { Outlet, useLocation } from 'react-router-dom';

import TopNav from '../containers/navs/Topnav';
import CompactSidebar from '../containers/navs/CompactSidebar';
import { resolveNavForPath } from '../constants/featureNavigation';
import ScreenSharePanel from '../components/ScreenSharePanel';
import DeviceSelectionPrompt from '../components/DeviceSelectionPrompt';
import ConsentBanner from '../components/ConsentBanner';
import MercyBanner from '../components/MercyBanner';
import AIControlBar from '../components/AIControlBar';
import SubjectStatusStrip from '../components/SubjectStatusStrip';
import QuickPunishment from '../components/QuickPunishment';
import CommandPalette from '../components/CommandPalette';
import ToastNotifications from '../components/ToastNotifications';
import ActionAuditLog, { ActionConfirmationToast } from '../components/ActionAuditLog';
import { DialogHost } from '../components/ui/dialog';
import { useWebRTC } from '../context/WebRTCContext';
import { useSocket, SOCKET_STATE } from '../context/SocketContext';
import { useScreenShare } from '../context/ScreenShareContext';
import { useTimelineEmit } from '../hooks/useTimelineEmit';
import { useTimelineBacklogSync } from '../hooks/useTimelineBacklogSync';
import './AppLayout.css';

// Views that don't require a selected device. The verb landings are included so
// an operator with nothing selected can still browse the feature catalog
// read-only (each landing degrades to a "connect a device to act" state)
// instead of being blocked by the device-selection prompt. Home is deliberately
// NOT here: when nothing is paired it should show the full pairing prompt, which
// is the first-run entry point.
const SAFE_ROUTES = [
  '/app/system/configuration',
  '/app/system/debug',
  '/app/interrogate',
  '/app/do',
  '/app/look-back',
  '/app/set-up',
];

/**
 * DisconnectedOverlay
 * Shows when there's no WebSocket connection at all (attempting to connect)
 */
const DisconnectedOverlay = ({ status }) => {
  // Determine if we're actively reconnecting or have an error
  const isReconnecting = status?.includes('Reconnecting');
  const isError = status?.includes('failed') || status?.includes('error');

  return (
    <div className="disconnected-overlay">
      <div className="disconnected-content">
        <div className="disconnected-icon-container">
          <div className="server-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <rect x="2" y="3" width="20" height="6" rx="1" />
              <rect x="2" y="15" width="20" height="6" rx="1" />
              <circle cx="6" cy="6" r="1" fill="currentColor" />
              <circle cx="6" cy="18" r="1" fill="currentColor" />
              <path d="M12 9v6" strokeLinecap="round" strokeDasharray="2 2" />
            </svg>
          </div>
          <div className="connecting-indicator">
            <div className="connecting-ring" />
          </div>
        </div>
        <h2>{isError ? 'Connection Failed' : 'Connecting to Server'}</h2>
        <p>
          {status || 'Establishing connection to the signaling server...'}
        </p>
        {isReconnecting && (
          <div className="disconnected-hint">
            <span className="hint-icon">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd"/>
              </svg>
            </span>
            <span>Automatically reconnecting...</span>
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Connection step indicator for debug display
 */
const ConnectionStep = ({ label, status, detail }) => {
  const icon = status === 'done' ? '✓' : status === 'active' ? '○' : status === 'error' ? '✗' : '·';
  const cls = `conn-step ${status}`;
  return (
    <div className={cls}>
      <span className="conn-step-icon">{icon}</span>
      <span className="conn-step-label">{label}</span>
      {detail && <span className="conn-step-detail">{detail}</span>}
    </div>
  );
};

/**
 * DeviceReconnectBanner
 * Non-blocking top banner when a device is selected but its DataChannel is disconnected.
 * Views beneath remain visible and interactive.
 */
const DeviceReconnectBanner = ({ deviceName, isWsOnline, connectionId, children }) => {
  const { peerNegotiationInfo, validateConnections, disconnect } = useWebRTC();
  const [elapsed, setElapsed] = useState(0);
  const [showDebug, setShowDebug] = useState(false);
  const startTimeRef = useRef(Date.now());

  // Timer to track how long we've been in this state
  useEffect(() => {
    startTimeRef.current = Date.now();
    setElapsed(0);
    const interval = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startTimeRef.current) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [connectionId, isWsOnline]);

  // Get negotiation info for the peer we're trying to connect to
  const negotiation = connectionId ? peerNegotiationInfo.get(connectionId) : null;

  // Derive connection steps
  const getSteps = () => {
    if (!isWsOnline) return [];
    const steps = [];
    steps.push({ label: 'Device visible to server', status: 'done' });

    if (negotiation?.offerReceived) {
      steps.push({ label: 'Offer received', status: 'done' });
    } else {
      steps.push({ label: 'Waiting for offer', status: elapsed > 5 ? 'error' : 'active', detail: elapsed > 5 ? 'Device may not know we are here' : null });
      return steps;
    }

    if (negotiation?.answerSent) {
      steps.push({ label: 'Answer sent', status: 'done' });
    } else if (negotiation?.error) {
      steps.push({ label: 'Answer failed', status: 'error', detail: negotiation.error });
      return steps;
    } else {
      steps.push({ label: 'Creating answer', status: 'active' });
      return steps;
    }

    const iceState = negotiation?.iceConnectionState || 'new';
    if (iceState === 'connected' || iceState === 'completed') {
      steps.push({ label: 'ICE connected', status: 'done' });
    } else if (iceState === 'failed') {
      steps.push({ label: 'ICE failed', status: 'error', detail: 'NAT/firewall may be blocking' });
      return steps;
    } else if (iceState === 'disconnected') {
      steps.push({ label: 'ICE disconnected', status: 'error' });
      return steps;
    } else {
      steps.push({ label: 'ICE negotiation', status: 'active', detail: iceState === 'checking' ? 'Testing pairs...' : iceState });
      return steps;
    }

    const dcState = negotiation?.dataChannelState || 'none';
    if (dcState === 'open') {
      steps.push({ label: 'DataChannel open', status: 'done' });
    } else {
      steps.push({ label: `DataChannel: ${dcState === 'none' ? 'waiting' : dcState}`, status: 'active' });
    }
    return steps;
  };

  const handleRetry = () => validateConnections();
  const handleFullReconnect = () => { disconnect(); setTimeout(() => window.location.reload(), 500); };

  const steps = isWsOnline ? getSteps() : [];
  const hasError = steps.some(s => s.status === 'error');
  const isStuck = elapsed >= 15;

  const statusMessage = isWsOnline
    ? `Connecting${deviceName ? ` to "${deviceName}"` : ''}...`
    : `${deviceName || 'Device'} is offline`;

  return (
    <div className={`reconnect-banner ${isWsOnline ? 'connecting' : 'offline'}`}>
      <div className="reconnect-banner-main">
        {isWsOnline && <div className="reconnect-banner-spinner" />}
        {!isWsOnline && (
          <svg className="reconnect-banner-icon" viewBox="0 0 16 16" fill="currentColor" width="14" height="14">
            <path fillRule="evenodd" d="M3.05 3.05a7 7 0 119.9 9.9 7 7 0 01-9.9-9.9zm1.414 8.486a5.5 5.5 0 007.072-7.072L4.464 11.536zm-1.06-1.06l7.072-7.072a5.5 5.5 0 00-7.072 7.072z"/>
          </svg>
        )}
        <span className="reconnect-banner-text">{statusMessage}</span>
        {elapsed >= 3 && <span className="reconnect-banner-elapsed">{elapsed}s</span>}
        {children && <div className="reconnect-banner-ai">{children}</div>}
        {(isStuck || hasError) && (
          <>
            <button className="reconnect-banner-action" onClick={handleRetry}>Retry</button>
            <button className="reconnect-banner-action secondary" onClick={handleFullReconnect}>Reconnect</button>
          </>
        )}
        <button
          className="reconnect-banner-toggle"
          onClick={() => setShowDebug(prev => !prev)}
        >
          {showDebug ? 'Hide details' : 'Show details'}
        </button>
      </div>
      {showDebug && isWsOnline && steps.length > 0 && (
        <div className="reconnect-banner-debug">
          {steps.map((step, i) => (
            <ConnectionStep key={i} {...step} />
          ))}
        </div>
      )}
    </div>
  );
};

const AppLayout = ({ containerClassnames }) => {
  const { isConnected, status } = useWebRTC();
  const { sockets, selectedSocketIndex } = useSocket();
  const { isPanelVisible } = useScreenShare();
  const emitTimelineEvent = useTimelineEmit();
  // Replay the Sacred Timeline's offline event queue on every (re)connect.
  useTimelineBacklogSync();
  const location = useLocation();
  const [isPaletteOpen, setIsPaletteOpen] = useState(false);
  const [isAuditOpen, setIsAuditOpen] = useState(false);
  // Mobile nav drawer state — a single React source of truth, replacing the old
  // scattered document.querySelector(...).classList.toggle() at three call sites.
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const closeMobileNav = useCallback(() => setMobileNavOpen(false), []);

  // Close the drawer on route change and on Escape.
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMobileNavOpen(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileNavOpen]);

  // Cmd+K / Ctrl+K to open command palette
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsPaletteOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Chrome that lives in the top bar (a search button, the audit-log button)
  // asks to open these global overlays via window events, so it doesn't need to
  // thread state up through TopNav. Names are namespaced to avoid collisions.
  useEffect(() => {
    const openPalette = () => setIsPaletteOpen(true);
    const openAudit = () => setIsAuditOpen(true);
    window.addEventListener('inquisitor:open-command-palette', openPalette);
    window.addEventListener('inquisitor:open-audit-log', openAudit);
    return () => {
      window.removeEventListener('inquisitor:open-command-palette', openPalette);
      window.removeEventListener('inquisitor:open-audit-log', openAudit);
    };
  }, []);

  const closePalette = useCallback(() => setIsPaletteOpen(false), []);

  // Track last emitted path to avoid duplicate navigation events on remount
  const lastEmittedPathRef = useRef(null);

  // Update page title based on current route + emit navigation event. The
  // resolver covers Home, the verb landings, the Arcade hub, and every game —
  // routes the old exact-`type.to` scan missed, leaving them titled a bare
  // "Inquisitor" and emitting no navigation event.
  useEffect(() => {
    const pageLabel = resolveNavForPath(location.pathname).title;
    document.title = pageLabel
      ? `${pageLabel} - Inquisitor`
      : 'Inquisitor';

    // Tell the desktop which feature the monitor is looking at (deduplicated)
    if (pageLabel && location.pathname !== lastEmittedPathRef.current) {
      lastEmittedPathRef.current = location.pathname;
      emitTimelineEvent('inquisitor.navigated', {
        title: `Navigated to ${pageLabel}`,
        data: { view: pageLabel, path: location.pathname }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- emitTimelineEvent is stable-by-intent; only fire on path change
  }, [location.pathname]);

  // Check if current route is safe (doesn't require connection)
  const isOnSafeRoute = SAFE_ROUTES.some(route => location.pathname.startsWith(route));

  // Analyze device states
  const hasSelectedDevice = selectedSocketIndex !== null;
  const selectedSocket = hasSelectedDevice ? sockets[selectedSocketIndex] : null;
  const selectedDeviceHasDataChannel = selectedSocket?.hasDataChannel === true;
  const hasBoundDevices = sockets.some(s =>
    s.state === SOCKET_STATE.BOUND_ONLINE || s.state === SOCKET_STATE.BOUND_OFFLINE
  );
  const hasPendingCodes = sockets.some(s => s.state === SOCKET_STATE.PENDING);
  const hasOnlineDevices = sockets.some(s => s.state === SOCKET_STATE.BOUND_ONLINE);

  // Determine what to show:
  // 1. Not connected at all -> show disconnected overlay
  // 2. Connected but no device selected -> show device selection prompt (replaces content)
  // 3. Connected and device selected -> show normal content

  const showDisconnectedOverlay = !isConnected && !isOnSafeRoute;

  // Show device selection prompt when:
  // - Connected to WebSocket
  // - NOT on a safe route (configuration, etc.)
  // - No device is currently selected
  const showDeviceSelectionPrompt = isConnected && !isOnSafeRoute && !hasSelectedDevice;

  // Show device reconnect banner when:
  // - Connected to WebSocket
  // - NOT on a safe route
  // - Device IS selected but DataChannel is disconnected
  const showDeviceReconnectBanner = isConnected && !isOnSafeRoute && hasSelectedDevice && !selectedDeviceHasDataChannel;

  return (
    <div className="app-layout">
      {/* Command Palette (Cmd+K) */}
      <CommandPalette isOpen={isPaletteOpen} onClose={closePalette} />

      {/* Quick Punishment (fixed bottom-right) */}
      <QuickPunishment />

      {/* Toast Notifications (fixed, floating above QuickPunishment) */}
      <ToastNotifications />

      {/* Action confirmation flash */}
      <ActionConfirmationToast />

      {/* Themed imperative dialogs (replaces native alert/confirm/prompt) */}
      <DialogHost />

      {/* Action Audit Log panel */}
      <ActionAuditLog isOpen={isAuditOpen} onClose={() => setIsAuditOpen(false)} />

      {/* Compact Sidebar */}
      <CompactSidebar mobileOpen={mobileNavOpen} onNavigate={closeMobileNav} />

      {/* Mobile sidebar backdrop */}
      <div
        className={`mobile-sidebar-backdrop${mobileNavOpen ? ' visible' : ''}`}
        onClick={closeMobileNav}
      />

      {/* Main Area */}
      <div className="app-main">
        {/* Top Navigation */}
        <TopNav mobileNavOpen={mobileNavOpen} onMobileMenuToggle={() => setMobileNavOpen(o => !o)} />

        {/* Subject Status Strip - only shown when device is connected with DataChannel */}
        {hasSelectedDevice && selectedDeviceHasDataChannel && (
          <SubjectStatusStrip>
            <AIControlBar />
          </SubjectStatusStrip>
        )}

        {/* Content Area */}
        <div className="app-content">
          {/* Reconnect banner - non-blocking top banner when device DataChannel is down.
              AIControlBar is intentionally NOT mounted inside it: the strip returns null
              while the DataChannel is down (its own !isConnected guard), so a second mount
              here only ever rendered an empty wrapper. The single strip lives in
              SubjectStatusStrip above. */}
          {showDeviceReconnectBanner && (
            <DeviceReconnectBanner
              deviceName={selectedSocket?.sessionName}
              isWsOnline={selectedSocket?.isWsOnline}
              connectionId={selectedSocket?.connectionId}
            />
          )}
          {/* Mercy handle — the safeword floor. Loudest possible signal; sits
              above consent because it supersedes everything. */}
          <MercyBanner />
          {/* Consent warnings (grace/expiry/self-release) — always visible to the operator */}
          <ConsentBanner />
          {/* Toast notifications moved to fixed floating position — see below */}
          {/* Screen share panel - a global, pinnable live view. Shown whenever a
              device is selected and the operator hasn't hidden it; it defaults
              collapsed (paused) on most routes and expanded on video-primary
              routes like the Screen page. Route-gating (SCREEN_SHARE_ROUTES) was
              removed so the live screen is available everywhere, not a random set. */}
          {isPanelVisible && hasSelectedDevice && (
            <ScreenSharePanel
              showCamera={false}
              showControls={true}
            />
          )}

          {/* Device Selection Prompt - replaces content when no device selected */}
          {showDeviceSelectionPrompt ? (
            <DeviceSelectionPrompt />
          ) : (
            /* Route content with standard padding */
            <div className="content-container">
              <Suspense fallback={<div className="content-loading"><div className="loading" /></div>}>
                <Outlet />
              </Suspense>
            </div>
          )}

          {/* Disconnection overlay - only when not connected at all */}
          {showDisconnectedOverlay && (
            <DisconnectedOverlay status={status} />
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = ({ menu }) => {
  const { containerClassnames } = menu;
  return { containerClassnames };
};

export default connect(mapStateToProps)(AppLayout);
